import {
  Button,
  Flex,
  Loader,
  ScrollView,
  SearchField,
} from "@aws-amplify/ui-react";
import { API, graphqlOperation, Storage } from "aws-amplify";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import LocalizedStrings from "react-localization";
import { CModal, CModalBody, CModalHeader, CModalTitle } from "@coreui/react";

import { equipmentTypeDocumentsByEquipmentTypeID } from "graphql/queries";
import { deleteEquipmentTypeDocument } from "graphql/mutations";
import usePermissions from "hooks/usePermission";
import ConfirmModal from "components/utils/ConfirmModal";
import EquipmentTypeParamatersTable from "components/equipmentTypes/documents/EquipmentTypeDocumentsTable";
import EquipmentTypeDocumentCreateForm from "./EquipmentTypeDocumentCreateForm";
import EquipmentTypeDocumentUpdateForm from "./EquipmentTypeDocumentUpdateForm";

const EquipmentTypeDocuments = ({
  equipmentTypeID,
  equipmentTypeName,
  visible,
  onClose,
}) => {
  const [canCreate, canUpdate, canDelete] = usePermissions([
    "equipment-type:create-doc",
    "equipment-type:update-doc",
    "equipment-type:delete-doc",
  ]);
  const [visibleState, setVisibleState] = useState(visible);
  const [equipmentTypeDocuments, setEquipmentTypeDocuments] = useState([]);
  const [equipmentTypeDocumentsLoading, setEquipmentTypeDocumentsLoading] =
    useState(false);
  const [paginationTokens, setPaginationTokens] = useState({
    previous: [],
    nextToken: null,
  });
  const [modalDetails, setModalDetails] = useState({
    visible: false,
    equipmentTypeDocumentId: null,
    equipmentTypeDocumentName: null,
  });
  const [modalEquipmentTypeDocument, setModalEquipmentTypeDocument] = useState({
    visible: false,
    equipmentTypeDocumentId: null,
  });

  const columnsMapped = useMemo(
    () =>
      equipmentTypeDocuments.map((item) => {
        return {
          ...item,
          actions: (
            <>
              {canUpdate && (
                <Button
                  className="px-1"
                  children={strings.actions.edit}
                  variation="link"
                  size="small"
                  onClick={() => openModal("update", item.id)}
                />
              )}
              <Button
                className="px-1"
                children={strings.actions.download}
                variation="link"
                size="small"
                onClick={() => downloadDoc(item.fileSrc)}
              />
              {canDelete && (
                <Button
                  className="px-1"
                  children={strings.actions.delete}
                  variation="link"
                  size="small"
                  onClick={() => {
                    setModalDetails({
                      visible: true,
                      equipmentTypeDocumentId: item.id,
                      equipmentTypeDocumentName: item.name,
                    });
                  }}
                />
              )}
            </>
          ),
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [equipmentTypeDocuments]
  );

  const onNext = () => {
    fetchEquipmentTypeDocuments(paginationTokens.nextToken);
  };

  const onPrevious = () => {
    const previous =
      paginationTokens.previous?.length <= 1
        ? null
        : paginationTokens.previous?.slice(-2)[0];
    fetchEquipmentTypeDocuments(previous, false);
  };

  const onKeywordSubmit = (keyword) => {
    const keywordSplit = keyword && keyword.split(" ");
    fetchEquipmentTypeDocuments(undefined, undefined, keywordSplit);
  };

  const openModal = (action, id) => {
    setVisibleState(false);
    setModalEquipmentTypeDocument({
      visible: true,
      action,
      equipmentTypeDocumentId: id,
    });
  };

  const closeModal = () => {
    setVisibleState(true);
    setModalEquipmentTypeDocument({
      visible: false,
    });
  };

  const onSuccessAction = () => {
    closeModal();
    fetchEquipmentTypeDocuments();
  };

  const downloadDoc = async (src) => {
    try {
      const res = await Storage.get(`equipment-types/${src}`, {
        level: "public",
      });
      const link = document.createElement("a");
      link.href = res;
      link.setAttribute("download", "");
      link.setAttribute("target", "_blank");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.log("Error Download Document", err);
      toast.error(strings.responses.errorDownload);
    }
  };

  async function fetchEquipmentTypeDocuments(token, isNext = true, keywords) {
    try {
      setEquipmentTypeDocumentsLoading(true);
      const limitMapped = 10;
      const equipmentTypeDocumentsData = await API.graphql(
        graphqlOperation(equipmentTypeDocumentsByEquipmentTypeID, {
          equipmentTypeID,
          limit: !keywords ? limitMapped : undefined,
          nextToken: token,
          filter: keywords
            ? {
                and: keywords.map((key) => ({
                  or: [
                    {
                      fileName: { contains: key },
                    },
                  ],
                })),
              }
            : {},
        })
      );
      const equipmentTypeDocumentsItems =
        equipmentTypeDocumentsData.data.equipmentTypeDocumentsByEquipmentTypeID.items;
      setEquipmentTypeDocuments(equipmentTypeDocumentsItems);
      setPaginationTokens((prevSt) => {
        let previous = prevSt.previous ?? [];
        if (isNext) previous = token ? [...previous, token] : [...previous];
        if (!isNext && previous?.length) previous.pop();

        return {
          previous,
          nextToken:
            equipmentTypeDocumentsData.data.equipmentTypeDocumentsByEquipmentTypeID
              .nextToken,
        };
      });
      setEquipmentTypeDocumentsLoading(false);
    } catch (err) {
      toast.error(strings.responses.errorFetch);
      setEquipmentTypeDocumentsLoading(false);
    } finally {
      setEquipmentTypeDocumentsLoading(false);
    }
  }

  async function equipmentTypeDocumentDelete(id) {
    try {
      const result = await API.graphql(
        graphqlOperation(deleteEquipmentTypeDocument, { input: { id } })
      );
      toast.success(strings.successDeleted);
      const { id: deletedId, fileSrc: deletedSrc } =
        result.data.deleteEquipmentTypeDocument;
      setEquipmentTypeDocuments(
        equipmentTypeDocuments.filter((item) => item.id !== deletedId)
      );
      setModalDetails({
        visible: false,
        equipmentTypeDocumentId: null,
      });

      if (deletedSrc)
        await Storage.remove(`equipment-types/${deletedSrc}`, {
          level: "public",
        });
    } catch (err) {
      toast.error(strings.responses[err]);
    }
  }

  useEffect(() => {
    fetchEquipmentTypeDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setVisibleState(visible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  return (
    <>
      <CModal
        size="xl"
        alignment="center"
        visible={visibleState}
        onClose={() => !modalEquipmentTypeDocument.visible && onClose()}
        backdrop="static"
      >
        <CModalHeader>
          <CModalTitle>
            {strings.title} - {equipmentTypeName}
          </CModalTitle>
        </CModalHeader>
        <CModalBody style={{ overflow: "hidden" }}>
          <ScrollView maxHeight="75vh">
            <div>
              <div className="p-3">
                <Flex className="mb-1" justifyContent="space-between">
                  <div style={styles.searchByKeyword}>
                    <SearchField
                      label={strings.table.search.label}
                      placeholder={strings.table.search.placeholder}
                      onSubmit={onKeywordSubmit}
                      onClear={onKeywordSubmit}
                    />
                  </div>
                  {canCreate && (
                    <Button
                      style={styles.buttonAdd}
                      children={strings.actions.addEquipmentTypeDocument}
                      variation="primary"
                      onClick={() => openModal("create")}
                    />
                  )}
                </Flex>
                {equipmentTypeDocumentsLoading ? (
                  <Loader />
                ) : (
                  <EquipmentTypeParamatersTable
                    items={columnsMapped}
                    pagination={paginationTokens}
                    onNext={onNext}
                    onPrevious={onPrevious}
                  />
                )}
              </div>
              <ConfirmModal
                visible={modalDetails.visible}
                body={strings.formatString(
                  strings.modal.confirmDeleteQuestion,
                  {
                    name: modalDetails.equipmentTypeDocumentName,
                  }
                )}
                onCancel={() =>
                  setModalDetails({
                    visible: false,
                    equipmentTypeDocumentId: null,
                  })
                }
                onConfirm={() =>
                  equipmentTypeDocumentDelete(modalDetails.equipmentTypeDocumentId)
                }
              />
            </div>
          </ScrollView>
        </CModalBody>
      </CModal>
      {modalEquipmentTypeDocument.action && (
        <CModal
          size="xl"
          alignment="center"
          visible={modalEquipmentTypeDocument.visible}
          onClose={closeModal}
          backdrop="static"
        >
          <CModalHeader>
            <CModalTitle>
              {modalEquipmentTypeDocument.action === "create" ? (
                <>
                  {strings.modalEquipmentTypeDocument.addTitle} - {equipmentTypeName}
                </>
              ) : (
                <>
                  {strings.modalEquipmentTypeDocument.updateTitle} - {equipmentTypeName}
                </>
              )}
            </CModalTitle>
          </CModalHeader>
          <CModalBody style={{ overflow: "hidden" }}>
            <ScrollView maxHeight="75vh">
              {modalEquipmentTypeDocument.action === "create" ? (
                <EquipmentTypeDocumentCreateForm
                  equipmentTypeID={equipmentTypeID}
                  onSuccess={onSuccessAction}
                />
              ) : (
                <EquipmentTypeDocumentUpdateForm
                  equipmentTypeDocumentId={
                    modalEquipmentTypeDocument.equipmentTypeDocumentId
                  }
                  onSuccess={onSuccessAction}
                />
              )}
            </ScrollView>
          </CModalBody>
        </CModal>
      )}
    </>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Documentos de Tipo de Equipo",
    successDeleted: "Documento de Tipo de Equipo eliminado con éxito",
    actions: {
      addEquipmentTypeDocument: "Agregar Documento de Tipo de Equipo",
      hide: "Ocultar",
      edit: "Editar",
      delete: "Eliminar",
      download: "Descargar",
    },
    responses: {
      errorFetch: "Error al consultar documento de tipos de equipo",
      errorDownload: "Error en la descarga",
    },
    table: {
      search: {
        label: "Buscar",
        placeholder: "Buscar por palabra clave...",
      },
    },
    modal: {
      confirmDeleteQuestion:
        "¿Estás seguro de eliminar el documento de tipo de equipo {name}?",
    },
    modalEquipmentTypeDocument: {
      addTitle: "Agregar Documento de Tipo de Equipo",
      updateTitle: "Actualizar Documento de Tipo de Equipo",
    },
  },
});

const styles = {
  buttonAdd: { height: "fit-content" },
  searchByKeyword: { marginBottom: "15px", width: "25%", minWidth: "300px" },
};

export default EquipmentTypeDocuments;
